import React, { useState, useMemo } from "react";
import ReactDOM from "react-dom";

const CAMPAIGN_NAMES = ['Lower Back Pain', 'Cervical/Neck Pain', 'Knee Pain', 'Sciatica'];

// Local copy of campaign matching (mirrors KIT.js matchCampaignPreset)
function autoDetectCampaign(lead) {
  const combined = [
    ...(lead.ailments || []),
    lead.enquiryDetails || '',
    lead.comments || '',
    lead.otherAilment || '',
  ].join(' ').toLowerCase();
  if (/sciatica|sciatic/.test(combined)) return 'Sciatica';
  if (/lower.?back|lumbar|lumbago|l4|l5|disc/.test(combined)) return 'Lower Back Pain';
  if (/cervical|neck|spondyl|c5|c6/.test(combined)) return 'Cervical/Neck Pain';
  if (/knee|patell|meniscus|acl|pcl/.test(combined)) return 'Knee Pain';
  return 'Lower Back Pain';
}

const CAMPAIGN_COLORS = {
  'Lower Back Pain':    { bg: '#fff7ed', color: '#c2410c' },
  'Cervical/Neck Pain': { bg: '#f0f9ff', color: '#0369a1' },
  'Knee Pain':          { bg: '#f0fdf4', color: '#15803d' },
  'Sciatica':           { bg: '#fdf4ff', color: '#7e22ce' },
};

/* ─────────────────────────────────────────────────────
   KITBulkActivate
   Props:
     leads           — all calllead objects
     campaignPresets — CAMPAIGN_PRESETS object from KIT
     onActivate(selectedLeads, campaignOverride) — bulk activate
     onSendCustom(selectedLeads, messageText)   — send a one-off message
     onClose()
────────────────────────────────────────────────────── */
export default function KITBulkActivate({ leads, campaignPresets, onActivate, onSendCustom, onClose }) {
  const [tab, setTab] = useState('activate'); // 'activate' | 'send'
  const [filterType, setFilterType] = useState('leads'); // 'leads' | 'patients' | 'all'
  const [search, setSearch] = useState('');
  const [filterAilment, setFilterAilment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [campaignOverride, setCampaignOverride] = useState('');
  const [customMsg, setCustomMsg] = useState('');
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(0);

  const probableLeadsCount = leads.filter(l => !l.patientCard).length;
  const enrolledCount = leads.filter(l => !!l.patientCard).length;

  // Collect all ailment values for filter
  const allAilments = useMemo(() => {
    const set = new Set();
    leads.forEach(l => (l.ailments || []).forEach(a => set.add(a)));
    return Array.from(set).sort();
  }, [leads]);

  const allStatuses = useMemo(() => {
    const set = new Set();
    leads.forEach(l => l.status && set.add(l.status));
    return Array.from(set).sort();
  }, [leads]);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      const name = (l.enquiredForName || l.name || '').toLowerCase();
      const num = String(l.number || '');
      const matchSearch = !search || name.includes(search.toLowerCase()) || num.includes(search);
      const matchAilment = !filterAilment || (l.ailments || []).some(a => a === filterAilment);
      const matchStatus = !filterStatus || l.status === filterStatus;
      const matchType = filterType === 'all'
        || (filterType === 'leads' ? !l.patientCard : !!l.patientCard);
      return matchSearch && matchAilment && matchStatus && matchType;
    });
  }, [leads, search, filterAilment, filterStatus, filterType]);

  // Leads not yet in KIT (for activate tab)
  const notInKIT = filteredLeads.filter(l => !l.kitActive);
  const inKIT = filteredLeads.filter(l => l.kitActive);
  const displayLeads = tab === 'activate' ? notInKIT : filteredLeads;

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === displayLeads.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayLeads.map(l => l._id)));
    }
  };

  const selectedLeads = displayLeads.filter(l => selected.has(l._id));

  const handleActivate = async () => {
    if (!selectedLeads.length) return;
    setProcessing(true);
    setDone(0);
    try {
      await onActivate(selectedLeads, campaignOverride || null, (n) => setDone(n));
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const handleSendCustom = async () => {
    if (!selectedLeads.length || !customMsg.trim()) return;
    setProcessing(true);
    try {
      await onSendCustom(selectedLeads, customMsg.trim());
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const inputStyle = { padding: '7px 10px', borderRadius: 7, border: '1.5px solid #d1d5db', fontSize: 13, outline: 'none', background: '#fff' };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 820, maxWidth: '97vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0d9488, #0891b2)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>🚀 Bulk KIT Actions</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{leads.filter(l => !l.kitActive).length} leads not yet in KIT · {leads.filter(l => l.kitActive).length} active</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Person Type Filter */}
        <div style={{ padding: '10px 16px 6px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>Show:</span>
          {[
            { id: 'leads',    label: '👤 Probable Leads',    count: probableLeadsCount },
            { id: 'patients', label: '🏥 Enrolled Patients', count: enrolledCount },
            { id: 'all',      label: '📋 All',               count: leads.length },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setFilterType(t.id); setSelected(new Set()); }}
              style={{
                padding: '4px 11px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                border: filterType === t.id ? 'none' : '1.5px solid #e2e8f0',
                background: filterType === t.id ? '#0d9488' : '#fff',
                color: filterType === t.id ? '#fff' : '#374151',
                fontWeight: filterType === t.id ? 700 : 500,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            >
              {t.label}
              <span style={{
                background: filterType === t.id ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
                color: filterType === t.id ? '#fff' : '#64748b',
                borderRadius: 10, padding: '0 5px', fontSize: 10, fontWeight: 700,
              }}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', background: '#f8fafc' }}>
          {[
            { id: 'activate', label: '🚀 Activate KIT', count: notInKIT.length },
            { id: 'send', label: '💬 Send to Selected', count: null },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSelected(new Set()); }}
              style={{
                padding: '10px 18px', border: 'none', background: 'none',
                fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
                color: tab === t.id ? '#0d9488' : '#6b7280',
                borderBottom: tab === t.id ? '2px solid #0d9488' : '2px solid transparent',
                cursor: 'pointer', marginBottom: -2,
              }}
            >
              {t.label}
              {t.count !== null && (
                <span style={{ marginLeft: 6, background: tab === t.id ? '#ccfbf1' : '#f3f4f6', color: tab === t.id ? '#0d9488' : '#6b7280', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ padding: '10px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
          <input
            type="text"
            placeholder="Search by name or number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, flex: 2, minWidth: 160 }}
          />
          <select value={filterAilment} onChange={e => setFilterAilment(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 120 }}>
            <option value="">All Ailments</option>
            {allAilments.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 120 }}>
            <option value="">All Statuses</option>
            {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Campaign override (activate tab only) */}
        {tab === 'activate' && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, background: '#fff', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', flexShrink: 0 }}>📋 Campaign for all selected:</span>
            <select
              value={campaignOverride}
              onChange={e => setCampaignOverride(e.target.value)}
              style={{ ...inputStyle, flex: 1, maxWidth: 280 }}
            >
              <option value="">✨ Auto-detect from ailments</option>
              {CAMPAIGN_NAMES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              {campaignOverride ? `All selected → ${campaignOverride}` : 'Each lead matched to their ailment automatically'}
            </span>
          </div>
        )}

        {/* Custom message (send tab only) */}
        {tab === 'send' && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Message (sent as: "Namaste [Name] Ji! [your text]")
            </div>
            <textarea
              rows={3}
              value={customMsg}
              onChange={e => setCustomMsg(e.target.value)}
              placeholder="Type your message here, or leave blank to pick a template per person…"
              style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {/* Lead list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Select all row */}
          <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={displayLeads.length > 0 && selected.size === displayLeads.length}
              onChange={toggleAll}
              style={{ width: 15, height: 15 }}
            />
            <span style={{ fontWeight: 600, color: '#374151' }}>
              {selected.size > 0 ? `${selected.size} selected` : `Select all (${displayLeads.length})`}
            </span>
            {tab === 'activate' && (
              <span style={{ color: '#6b7280', marginLeft: 'auto' }}>
                🔴 {notInKIT.length} not in KIT · 🟢 {inKIT.length} already active (excluded)
              </span>
            )}
          </div>

          {displayLeads.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              {tab === 'activate' ? 'All filtered leads are already in KIT.' : 'No leads match the current filters.'}
            </div>
          )}

          {displayLeads.map(lead => {
            const name = lead.enquiredForName || lead.name || 'Unknown';
            const isChecked = selected.has(lead._id);
            const ailments = (lead.ailments || []).slice(0, 2);
            const detectedCampaign = campaignOverride || autoDetectCampaign(lead);
            const campaignStyle = CAMPAIGN_COLORS[detectedCampaign] || { bg: '#f3f4f6', color: '#374151' };
            return (
              <div
                key={lead._id}
                onClick={() => toggleSelect(lead._id)}
                style={{
                  padding: '8px 16px', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                  background: isChecked ? '#f0fdfa' : '#fff',
                  transition: 'background 0.1s',
                }}
              >
                <input type="checkbox" checked={isChecked} onChange={() => toggleSelect(lead._id)} onClick={e => e.stopPropagation()} style={{ width: 15, height: 15, flexShrink: 0 }} />
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: isChecked ? '#0d9488' : '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: isChecked ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>
                  {name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
                    {lead.patientCard && (
                      <span style={{ fontSize: 9, background: '#dbeafe', color: '#1d4ed8', padding: '1px 5px', borderRadius: 6, fontWeight: 700 }}>
                        🏥 Patient
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    📱 {lead.number || '—'}
                    {lead.city && <> · {lead.city}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 150 }}>
                  {ailments.map(a => (
                    <span key={a} style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: 8 }}>{a}</span>
                  ))}
                  {(lead.ailments || []).length > 2 && (
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>+{lead.ailments.length - 2}</span>
                  )}
                </div>
                {/* Auto-campaign badge (activate tab) */}
                {tab === 'activate' && !lead.kitActive && (
                  <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 90 }}>
                    <div style={{ fontSize: 10, background: campaignStyle.bg, color: campaignStyle.color, padding: '2px 7px', borderRadius: 8, fontWeight: 700 }}>
                      {campaignOverride ? '📋' : '✨'} {detectedCampaign.replace(' Pain', '').replace('/Neck', '')}
                    </div>
                    <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>
                      {campaignOverride ? 'override' : 'auto-matched'}
                    </div>
                  </div>
                )}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: lead.kitActive ? '#0d9488' : '#6b7280' }}>
                    {lead.kitActive ? `🚀 ${lead.kitCampaign || 'KIT Active'}` : lead.status || '—'}
                  </div>
                  {lead.lastContactedDate && (
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>
                      Last: {new Date(lead.lastContactedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', gap: 8, alignItems: 'center' }}>
          {processing ? (
            <div style={{ flex: 1, fontSize: 13, color: '#0d9488', fontWeight: 600 }}>
              ⏳ Processing {done}/{selectedLeads.length} leads…
            </div>
          ) : (
            <div style={{ flex: 1, fontSize: 12, color: '#6b7280' }}>
              {selected.size > 0 ? `${selected.size} lead${selected.size !== 1 ? 's' : ''} selected` : 'Select leads above to continue'}
            </div>
          )}
          {tab === 'activate' && (
            <button
              onClick={handleActivate}
              disabled={!selectedLeads.length || processing}
              style={{
                padding: '9px 18px', background: selectedLeads.length ? '#0d9488' : '#d1d5db',
                color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: selectedLeads.length ? 'pointer' : 'not-allowed',
              }}
            >
              🚀 Activate KIT for {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}
            </button>
          )}
          {tab === 'send' && (
            <button
              onClick={handleSendCustom}
              disabled={!selectedLeads.length || !customMsg.trim() || processing}
              style={{
                padding: '9px 18px',
                background: (selectedLeads.length && customMsg.trim()) ? '#25D366' : '#d1d5db',
                color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                cursor: (selectedLeads.length && customMsg.trim()) ? 'pointer' : 'not-allowed',
              }}
            >
              💬 Queue Send for {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}
            </button>
          )}
          <button
            onClick={onClose}
            style={{ padding: '9px 16px', background: '#fff', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
